import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { getSharedKtx2Loader } from '../../../loaders/sharedKtx2Loader';

const BASIS_TRANSCODER_PATH = '/basis/';
let basisAvailabilityPromise = null;
let sharedKtx2Loader = null;
let sharedKtx2Gl = null;
const textureCache = new Map();
const textureCacheOrder = [];
const MAX_CACHED_TEXTURES = 24;

function touchCacheKey(key) {
  const idx = textureCacheOrder.indexOf(key);
  if (idx >= 0) textureCacheOrder.splice(idx, 1);
  textureCacheOrder.push(key);
}

function putTextureInCache(key, tex) {
  textureCache.set(key, tex);
  touchCacheKey(key);

  while (textureCacheOrder.length > MAX_CACHED_TEXTURES) {
    const oldest = textureCacheOrder.shift();
    if (!oldest) break;
    const oldTex = textureCache.get(oldest);
    textureCache.delete(oldest);
    oldTex?.dispose?.();
  }
}

function getCachedFinishingTexture(resolved) {
  if (!resolved) return null;
  return textureCache.get(`finishing:${resolved}`) ?? null;
}

function hasBasisTranscoderFiles() {
  if (basisAvailabilityPromise) return basisAvailabilityPromise;

  // CRA dev-server может отдавать index.html c 200 на несуществующие пути.
  // Проверяем, что /basis/* действительно НЕ html.
  basisAvailabilityPromise = Promise.all([
    fetch(`${BASIS_TRANSCODER_PATH}basis_transcoder.wasm`, { cache: 'no-store' }),
    fetch(`${BASIS_TRANSCODER_PATH}basis_transcoder.js`, { cache: 'no-store' })
  ])
    .then(async ([wasmRes, jsRes]) => {
      if (!wasmRes.ok || !jsRes.ok) return false;

      const wasmType = wasmRes.headers.get('content-type') || '';
      const jsType = jsRes.headers.get('content-type') || '';
      if (wasmType.includes('text/html') || jsType.includes('text/html')) return false;

      const [wasmBuf, jsText] = await Promise.all([wasmRes.arrayBuffer(), jsRes.text()]);
      const wasmMagic =
        wasmBuf.byteLength >= 4 &&
        new Uint8Array(wasmBuf, 0, 4).join(',') === '0,97,115,109'; // \0asm

      const jsLooksHtml = jsText.trim().startsWith('<');
      return wasmMagic && !jsLooksHtml;
    })
    .catch(() => false);

  return basisAvailabilityPromise;
}

function getKtx2Candidate(url) {
  if (!url) return null;
  // Для отделки не делаем авто-подмену jpg/png -> ktx2:
  // на сайте может существовать некорректный .ktx2 и чаша становится черной.
  if (/\.ktx2(\?.*)?$/i.test(url)) return url;
  return null;
}

/**
 * Загрузка текстуры отделки без React Suspense (в отличие от useTexture/useLoader).
 * При смене URL предыдущая текстура остаётся на чаше, пока не загрузится новая — сцена не пропадает.
 */
export function usePoolFinishingTexture(url) {
  const { gl } = useThree();
  const resolved = url || '/models/pools/plaster.webp';
  const [texture, setTexture] = useState(() => getCachedFinishingTexture(resolved));
  const targetAnisotropy = useMemo(() => {
    const maxSupported = gl?.capabilities?.getMaxAnisotropy?.() ?? 4;
    // Адаптивный cap: визуально почти как 16, но заметно дешевле для GPU.
    return Math.max(1, Math.min(maxSupported, 8));
  }, [gl]);

  const neutral = useMemo(() => {
    const data = new Uint8Array([210, 210, 210, 255]);
    const t = new THREE.DataTexture(data, 1, 1);
    t.needsUpdate = true;
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    return t;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fallbackLoader = new THREE.TextureLoader();

    const applyAndCommit = (tex) => {
      if (cancelled) {
        tex.dispose?.();
        return;
      }
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      // Цветные карты отделки должны быть в sRGB.
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = targetAnisotropy;
      tex.needsUpdate = true;
      setTexture(tex);
    };

    const loadFallback = (cacheKey) => {
      fallbackLoader.load(
        resolved,
        (tex) => {
          putTextureInCache(cacheKey, tex);
          applyAndCommit(tex);
        },
        undefined,
        (err) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('usePoolFinishingTexture (fallback):', resolved, err);
          }
        }
      );
    };

    // Источник остаётся URL с сайта; если уже декодировали ранее — берем из кэша без повторной загрузки.
    const cacheKey = `finishing:${resolved}`;
    const cached = textureCache.get(cacheKey);
    if (cached) {
      touchCacheKey(cacheKey);
      applyAndCommit(cached);
      return () => {
        cancelled = true;
      };
    }

    const ktx2Candidate = getKtx2Candidate(resolved);
    const canTryKtx2 = !!gl && !!ktx2Candidate;

    if (!canTryKtx2) {
      loadFallback(cacheKey);
      return () => {
        cancelled = true;
      };
    }

    hasBasisTranscoderFiles().then((basisReady) => {
      if (cancelled) return;
      if (!basisReady) {
        loadFallback(cacheKey);
        return;
      }

      const ktx2Loader = getSharedKtx2Loader(gl);
      if (!ktx2Loader) {
        loadFallback(cacheKey);
        return;
      }
      ktx2Loader.load(
        ktx2Candidate,
        (tex) => {
          putTextureInCache(cacheKey, tex);
          applyAndCommit(tex);
        },
        undefined,
        () => {
          // Если .ktx2 не найден или не поддержан — бесшовно уходим на исходный JPG/PNG.
          loadFallback(cacheKey);
        }
      );
    });

    return () => {
      cancelled = true;
    };
  }, [resolved, neutral, gl, targetAnisotropy]);

  useEffect(() => {
    return () => {
      setTexture(null);
    };
  }, []);

  return texture ?? neutral;
}
